import { useRef, useState } from 'react';
import { errorMessage } from '@/lib/api';
import { MAX_IMAGE_BYTES } from '@/services/catalogImageService';

export interface PhotoSet {
  images: string[];
  imageKeys: string[];
}

interface ImageManagerProps {
  title: string;
  photos: PhotoSet;
  onUpload: (file: File) => Promise<PhotoSet>;
  onRemove: (key: string) => Promise<PhotoSet>;
  onChange: (photos: PhotoSet) => void;
  onError: (message: string) => void;
}

/**
 * Photography for one catalogue row. The URLs handed in are short-lived signed
 * links, so nothing here stores them beyond the render — every change takes
 * the fresh list the server returns.
 */
export default function ImageManager({
  title,
  photos,
  onUpload,
  onRemove,
  onChange,
  onError,
}: ImageManagerProps) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const busy = uploading || removing !== null;
  const { images, imageKeys } = photos;

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy) return;
    if (file.size > MAX_IMAGE_BYTES) {
      onError(`${file.name} is larger than 10 MB.`);
      return;
    }
    setUploading(true);
    try {
      onChange(await onUpload(file));
    } catch (error) {
      onError(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (key: string) => {
    if (busy) return;
    setRemoving(key);
    try {
      onChange(await onRemove(key));
    } catch (error) {
      onError(errorMessage(error));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase">{title}</span>
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          className="text-xs font-semibold text-babcock-navy-700 hover:underline disabled:opacity-50 cursor-pointer"
        >
          <i className={uploading ? 'ri-loader-4-line animate-spin mr-1' : 'ri-upload-2-line mr-1'}></i>
          {uploading ? 'Uploading…' : 'Add photo'}
        </button>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {images.length === 0 ? (
        <p className="text-xs text-gray-400">No photography yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {images.map((url, index) => (
            <div key={imageKeys[index] ?? url.slice(0, 60)} className="relative group">
              <img
                src={url}
                alt={`${title} ${index + 1}`}
                loading="lazy"
                className="w-full h-20 object-cover rounded-md border border-gray-200"
              />
              <button
                type="button"
                aria-label={`Remove photo ${index + 1}`}
                onClick={() => void handleRemove(imageKeys[index])}
                disabled={busy || !imageKeys[index]}
                className="absolute top-1 right-1 bg-white/90 rounded-full w-6 h-6 flex items-center justify-center text-gray-500 hover:text-red-600 disabled:opacity-50 cursor-pointer"
              >
                <i className={removing === imageKeys[index] ? 'ri-loader-4-line animate-spin' : 'ri-close-line'}></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
